import { Link } from 'react-router-dom';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import SafeImage from '../../components/SafeImage';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import BlockedMembersSection from './BlockedMembersSection';
import { selfCreatorPath } from './meIaNav';
import './me-page.css';

/**
 * Me Hub IA v2（FE-IA-V2-04）。
 * Canonical：Profile／收藏／個人地圖／VIP／Settings。
 * 不造假 VIP 頁或 Saved location；既有帳號工具保留在次要區塊。
 */
export default function MePage() {
  const { user, logout } = useAuth();
  const { t } = useTravelTranslation();
  const profileTo = selfCreatorPath(user);
  const avatar = resolveMemberMediaUrl(user?.avatarUrl || user?.avatar);
  const displayName = travelMemberProductDisplayName(user, {
    genericFallback: t('travel.nav.member'),
  });

  return (
    <main className="me-page me-page--ia-v2" data-testid="me-page" data-me-ia="v2">
      <header className="me-page__header">
        <SafeImage className="me-page__avatar" src={avatar} alt="" />
        <div className="me-page__identity">
          <p className="me-page__eyebrow">{t('travel.nav.me')}</p>
          <h1 className="me-page__title">{displayName}</h1>
          {profileTo ? (
            <Link
              className="me-page__edit-entry"
              to={`${profileTo}?edit=1`}
              data-testid="me-header-edit-profile"
            >
              {t('travel.creator.editProfile')}
            </Link>
          ) : null}
        </div>
      </header>

      <section className="me-page__section" aria-labelledby="me-ia-canonical-title">
        <h2 id="me-ia-canonical-title" className="me-page__section-title">
          {t('travel.me.hub.canonicalLabel')}
        </h2>
        <nav className="me-page__nav me-page__nav--canonical" aria-label={t('travel.me.hub.canonicalLabel')} data-testid="me-ia-canonical-nav">
          {profileTo ? (
            <>
              <Link className="me-page__link" to={profileTo} data-testid="me-link-profile">
                <span>{t('travel.nav.profile')}</span>
                <span className="me-page__link-hint">{t('travel.me.hub.profileHint')}</span>
              </Link>
              <Link
                className="me-page__link me-page__link--emphasis"
                to={`${profileTo}?edit=1`}
                data-testid="me-link-edit-profile"
              >
                <span>{t('travel.creator.editProfile')}</span>
                <span className="me-page__link-hint">{t('travel.me.hub.editProfileHint')}</span>
              </Link>
            </>
          ) : null}
          <Link className="me-page__link" to="/saved" data-testid="me-link-saved">
            {t('travel.nav.saved')}
          </Link>
          <Link className="me-page__link" to="/me/personal-map" data-testid="me-link-personal-map">
            <span>{t('travel.nav.personalMap')}</span>
            <span className="me-page__link-hint">{t('travel.nav.personalMapHint')}</span>
          </Link>
          <Link className="me-page__link" to="/me/publication-consents" data-testid="me-link-publication-consents">
            <span>{t('travel.publication.story.mine.title')}</span>
            <span className="me-page__link-hint">{t('travel.publication.story.mine.meLinkHint')}</span>
          </Link>
          <div
            className="me-page__link me-page__link--unavailable"
            data-testid="me-link-vip"
            data-capability="NOT_AVAILABLE"
            role="group"
            aria-label={`${t('travel.me.vip.title')}：${t('travel.me.vip.unavailableHint')}`}
          >
            <span>{t('travel.me.vip.title')}</span>
            <span className="me-page__link-hint">{t('travel.me.vip.unavailableHint')}</span>
          </div>
        </nav>
      </section>

      <section
        className="me-page__section me-page__settings"
        aria-labelledby="me-settings-title"
        data-testid="me-settings"
        id="me-settings"
      >
        <h2 id="me-settings-title" className="me-page__section-title">
          {t('travel.me.settings.title')}
        </h2>
        <nav className="me-page__nav me-page__nav--settings" aria-label={t('travel.me.settings.title')}>
          <a className="me-page__link me-page__link--compact" href="#me-settings-blocked" data-testid="me-link-settings-blocked">
            {t('travel.me.settings.blocked')}
          </a>
          <Link className="me-page__link me-page__link--compact" to="/privacy-policy" data-testid="me-link-settings-privacy">
            {t('travel.me.settings.privacy')}
          </Link>
          <Link className="me-page__link me-page__link--compact" to="/agreement" data-testid="me-link-settings-agreement">
            {t('travel.me.settings.agreement')}
          </Link>
        </nav>
        <div id="me-settings-blocked">
          <BlockedMembersSection />
        </div>
      </section>

      <section className="me-page__section" aria-labelledby="me-account-tools-title">
        <h2 id="me-account-tools-title" className="me-page__section-title">
          {t('travel.me.hub.accountLabel')}
        </h2>
        <nav className="me-page__nav me-page__nav--account" aria-label={t('travel.me.hub.accountLabel')} data-testid="me-account-tools-nav">
          <Link className="me-page__link me-page__link--compact" to="/journeys" data-testid="me-link-journeys">
            {t('travel.nav.myJourneys')}
          </Link>
          <Link className="me-page__link me-page__link--compact" to="/group-trips" data-testid="me-link-group-trips">
            {t('travel.nav.groupTrips')}
          </Link>
          <Link className="me-page__link me-page__link--compact" to="/invitations" data-testid="me-link-invitations">
            {t('travel.nav.invitations')}
          </Link>
          <Link className="me-page__link me-page__link--compact" to="/host/earnings" data-testid="me-link-earnings">
            {t('travel.nav.earnings')}
          </Link>
          <Link className="me-page__link me-page__link--compact" to="/notifications" data-testid="me-link-notifications">
            {t('travel.nav.notificationCenter')}
          </Link>
          <Link className="me-page__link me-page__link--compact" to="/me/ads" data-testid="me-link-ads">
            {t('travel.nav.ads')}
          </Link>
        </nav>
      </section>

      <button type="button" className="me-page__sign-out" onClick={logout} data-testid="me-sign-out">
        {t('travel.nav.signOut')}
      </button>
    </main>
  );
}
